import { useState } from "react";
import { api } from "../../../lib/api";
import { toast } from "sonner";
import { Truck, CaretDown, CaretRight } from "@phosphor-icons/react";
import { Panel } from "./_shared";

// M3 — SUPER_ADMIN origin-pickup completion. Reuses the EXISTING Rider pickup-complete endpoint and
// request schema; the backend remains authoritative and enforces every workflow prerequisite.
// SUPER_ADMIN only. Riders keep using their own /rider workspace unchanged. (2026-10: the dead
// pickup-measure Save — unregistered route, Module 9A parked — was removed from this panel.)
const CHECKLIST_ITEMS = [
  ["count_verified", "Package count verified"],
  ["condition_ok", "Package condition OK"],
  ["closure_condition_recorded", "Closure condition recorded"],
  ["photos_uploaded", "Pickup photos uploaded"],
  ["label_visible", "Label visible"],
  ["handover_ack", "Handover acknowledged"],
];

export function SuperAdminPickupPanel({ d, reload }) {
  const shipmentId = d.shipment.id;
  // Reuse the EXACT RiderHome completion-stage rule (RiderHome.jsx: completionStageOk). This panel is an
  // ACTION surface — it is not mounted outside the pickup-completion stage. Backend is authoritative.
  const status = d.shipment.current_status;
  const pickupDone = !!d.shipment.pickup_completed_at;
  const completionStageOk = !pickupDone && status === "SOURCE_VEHICLE_ASSIGNED";
  const [open, setOpen] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [checklist, setChecklist] = useState({
    count_verified: false,
    condition_ok: false,
    closure_condition_recorded: false,
    closure_type: "CELLO_TAPE",
    closure_notes: "",
    photos_uploaded: false,
    label_visible: false,
    handover_ack: false,
    wet_signed_declarations_obtained: false,
    notes: "",
  });

  if (!completionStageOk) return null;

  const submitComplete = async () => {
    if (!checklist.wet_signed_declarations_obtained) {
      return toast.error("Confirm the wet-signed declarations were obtained before completing pickup.");
    }
    if (!window.confirm("Complete pickup for this shipment as Super Admin? This records origin custody.")) return;
    setSubmitting(true);
    try {
      const { data } = await api.post(`/rider/shipments/${shipmentId}/pickup-complete`, checklist);
      toast.success(data?.idempotent ? "Pickup is already completed." : "Pickup complete — custody recorded");
      reload();
    } catch (e) {
      const det = e?.response?.data?.detail;
      toast.error((det && det.message) || e.displayMessage || "Could not complete pickup.");
      reload();
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Panel
      icon={<Truck size={16} weight="bold" />}
      title="ORIGIN PICKUP OPERATIONS (SUPER ADMIN)"
      action={
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          data-testid="sa-pickup-toggle"
          className="text-[11px] inline-flex items-center gap-1 text-[hsl(var(--muted))] hover:text-[hsl(var(--fg))]"
        >
          {open ? <CaretDown size={12} /> : <CaretRight size={12} />}
          {open ? "Hide" : "Perform Rider pickup actions"}
        </button>
      }
    >
      {!open ? (
        <p className="text-[11px] text-[hsl(var(--muted))]" data-testid="sa-pickup-hint">
          Super Admin may complete origin Rider pickup. Backend enforces all
          workflow prerequisites; actions are attributed to the Super Admin performing them.
        </p>
      ) : (
        <div className="space-y-4" data-testid="sa-pickup-panel">
          {/* Pickup completion checklist — the panel only mounts in the completion stage (SOURCE_VEHICLE_ASSIGNED) */}
          <div className="space-y-1">
            <p className="text-[11px] font-semibold">Pickup completion</p>
            {CHECKLIST_ITEMS.map(([k, label]) => (
              <label key={k} className="flex items-center gap-2 text-[11px]">
                <input
                  type="checkbox"
                  checked={checklist[k]}
                  onChange={(e) => setChecklist({ ...checklist, [k]: e.target.checked })}
                  data-testid={`sa-checklist-${k}`}
                />
                <span>{label}</span>
              </label>
            ))}
            {checklist.closure_condition_recorded && (
              <select
                value={checklist.closure_type}
                onChange={(e) => setChecklist({ ...checklist, closure_type: e.target.value })}
                data-testid="sa-closure-type"
                className="px-1 py-0.5 text-[11px] rounded border border-[hsl(var(--border))] bg-transparent"
              >
                <option value="CELLO_TAPE">Cello tape</option>
                <option value="GUM_TAPE">Gum tape</option>
                <option value="STRAPPED">Strapping band</option>
                <option value="OTHER">Other</option>
              </select>
            )}
            <label className="flex items-center gap-2 text-[11px] mt-1">
              <input
                type="checkbox"
                checked={checklist.wet_signed_declarations_obtained}
                onChange={(e) => setChecklist({ ...checklist, wet_signed_declarations_obtained: e.target.checked })}
                data-testid="sa-checklist-wet_signed_declarations_obtained"
              />
              <span>Required printed declarations were <strong>wet-signed</strong>.</span>
            </label>
            <button
              type="button"
              disabled={submitting || !checklist.wet_signed_declarations_obtained}
              onClick={submitComplete}
              data-testid="sa-submit-pickup-complete-btn"
              className="mt-1 px-3 py-1 text-[11px] rounded bg-[hsl(var(--fg))] text-[hsl(var(--bg))] disabled:opacity-50"
            >
              {submitting ? "Completing…" : "Complete pickup"}
            </button>
          </div>
        </div>
      )}
    </Panel>
  );
}
